import { ReactNode } from 'react';
import { orderBy, where } from 'firebase/firestore';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { Commit, commitCoverter, COMMIT_COLLECTION_NAME } from 'models';
const LIMIT = 10;
export interface RecursiveProviderProps {
    children: ReactNode | undefined;
    indexIds: string[];
}

export interface RecursiveCollectorProps extends RecursiveProviderProps {
    contents: CollectionState<Commit>[];
    children: (_: CollectionState<Commit>[]) => JSX.Element;
}
const RecursiveCollector = ({ indexIds, children, contents }: RecursiveCollectorProps) => {
    const batch = indexIds.slice(0, LIMIT);
    const next = indexIds.slice(LIMIT);
    const { collectionName, constraints } = providerProps(batch);
    const content = useCollectionContext(collectionName, constraints) as CollectionState<Commit>;
    if (next.length === 0) {
        return <>{children(contents.concat([content]))}</>;
    }
    return (
        <RecursiveCollector
            indexIds={next}
            contents={contents.concat([content])}
            children={children}
        />
    );
};

const providerProps = (indexIds: string[]) => {
    return {
        collectionName: COMMIT_COLLECTION_NAME,
        converter: commitCoverter,
        constraints: [where('targetIndexId', 'in', indexIds), orderBy('createdAt', 'desc')],
    };
};

const RecursiveProvider = ({ indexIds, children }: RecursiveProviderProps) => {
    if (indexIds.length === 0) {
        return <>{children}</>;
    }
    const batch = indexIds.slice(0, LIMIT);
    const next = indexIds.slice(LIMIT);
    if (next.length > 0) {
        return (
            <CollectionProvider {...providerProps(batch)}>
                <RecursiveProvider indexIds={next}>{children}</RecursiveProvider>
            </CollectionProvider>
        );
    }
    return (
        <CollectionProvider {...providerProps(batch)}>
            <div>{children}</div>
        </CollectionProvider>
    );
};

export const ContractCommitsProvider = RecursiveProvider;
export interface ContractCommitsProviderProps extends RecursiveProviderProps {}
export const ContractCommitsCollector = RecursiveCollector;
